import { Component, ViewChild, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { GoogleMapComponent } from '@shared/components';
import { MapHelperService } from '@shared/services';
import { ViewBasePage } from '@shared/pages';
import type { TRoutesViewModel } from '@shared/models';

@Component({
    selector: 'app-route5Verification',
    templateUrl: './route5Verification.page.html',
    styleUrls: ['./route5Verification.page.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, IonicModule, GoogleMapComponent]
})
export class Route5VerificationPage extends ViewBasePage implements OnInit, OnDestroy {
    @ViewChild(GoogleMapComponent) public mapComponent!: GoogleMapComponent;

    public vm!: TRoutesViewModel;
    public dealershipCoordinate!: { latitude: number; longitude: number };

    // ✅ Constants for magic strings
    private readonly ROUTE_PATHS = {
        NEXT: '/routes/route6Save'
    } as const;

    constructor(
        private mapHelperService: MapHelperService,
        injector: Injector
    ) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(this.Constants.VIEW_ROUTE_PAGE5);
    }

    public override async ngOnInit(): Promise<void> {
        super.ngOnInit();
        this.vm = super.getViewModel();

        // Set dealership coordinate for map component
        this.dealershipCoordinate = {
            latitude: this.vm.dealership.address.coordinate.latitude,
            longitude: this.vm.dealership.address.coordinate.longitude
        };
    }

    public override ngOnDestroy(): void {
        // GoogleMapComponent handles cleanup automatically
        super.ngOnDestroy();
    }

    // ================================================
    // Navigation & UI Actions
    // ================================================

    public onNextClick(): void {
        super.setViewModel(this.vm);
        this.router.navigateByUrl(this.ROUTE_PATHS.NEXT);
    }

    public onCenterMeClick(): void {
        if (this.mapComponent && this.dealershipCoordinate) {
            this.mapComponent.centerMap(this.dealershipCoordinate);
        }
    }

    // ================================================
    // Map Ready Event - Called when map is initialized
    // ================================================

    /**
     * Handle map ready event - add markers and routes
     * GoogleMapComponent has initialized and is ready for use
     */
    public async onMapReady(): Promise<void> {
        await this.uxHelperService.showSpinner('Loading map.');

        try {
            // Step 1: Add dealership marker
            await this.createDealershipMarker();

            // Step 2: Add start and end markers
            await this.createStartAndEndMarkers();

            // Step 3: Add destination markers (A, B, C, etc.)
            await this.createDestinationMarkers();

            // Step 4: Draw routes
            await this.calculateRoutes();

            await this.uxHelperService.hideSpinner();
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        }
    }

    /**
     * Handle map error event
     */
    public onMapError(event: { message: string; error?: unknown }): void {
        // console.error('Map error:', event);
        this.uxHelperService.showAlert('Map Error', event.message);
    }

    // ================================================
    // Marker Creation - Using GoogleMapComponent
    // ================================================

    /**
     * Create dealership marker with star icon
     */
    private async createDealershipMarker(): Promise<void> {
        const dealershipName = this.vm.dealership.dealershipName;
        const iconUrl = this.mapHelperService.getMarkerIcon('Dealership');

        this.mapComponent.addDealershipMarker(
            this.dealershipCoordinate,
            dealershipName,
            iconUrl
        );
    }

    /**
     * Create start and end markers
     */
    private async createStartAndEndMarkers(): Promise<void> {
        const startingPoint = this.vm.startingPoint;
        const endingPoint = this.vm.endingPoint;

        // Determine if same address
        const isSameAddress = (
            startingPoint.location.address.streetAddress ===
            endingPoint.location.address.streetAddress
        );

        // Get appropriate icons
        const startIcon = isSameAddress
            ? this.mapHelperService.getMarkerIcon('startNW')
            : this.mapHelperService.getMarkerIcon('startNS');

        const endIcon = isSameAddress
            ? this.mapHelperService.getMarkerIcon('endNE')
            : this.mapHelperService.getMarkerIcon('endNS');

        // Add start marker
        this.mapComponent.addStartMarker(
            {
                latitude: startingPoint.location.address.coordinate.latitude,
                longitude: startingPoint.location.address.coordinate.longitude
            },
            startingPoint.location.locationName,
            startingPoint.location.address.fullAddress,
            startIcon
        );

        // Add end marker
        this.mapComponent.addEndMarker(
            {
                latitude: endingPoint.location.address.coordinate.latitude,
                longitude: endingPoint.location.address.coordinate.longitude
            },
            endingPoint.location.locationName,
            endingPoint.location.address.fullAddress,
            endIcon
        );
    }

    /**
     * Create destination markers with letter labels (A, B, C, etc.)
     */
    private async createDestinationMarkers(): Promise<void> {
        for (const leg of this.vm.routeLegs) {
            // Skip special markers (start/end have their own icons)
            if (leg.letter === '?') {
                continue;
            }

            const coordinate = {
                latitude: leg.to.location.address.coordinate.latitude,
                longitude: leg.to.location.address.coordinate.longitude
            };

            const title = `${leg.passengerName}. ${leg.to.location.address.fullAddress}`;

            this.mapComponent.addLetterMarker(leg.letter, coordinate, title);
        }
    }

    // ================================================
    // Route Calculation - Using GoogleMapComponent
    // ================================================

    /**
     * Calculate and display routes for all route legs
     */
    private async calculateRoutes(): Promise<void> {
        const routePromises = this.vm.routeLegs.map(leg =>
            this.mapComponent.calculateRoute({
                origin: leg.from.location.address.fullAddress,
                destination: leg.to.location.address.fullAddress
            })
        );

        try {
            await Promise.all(routePromises);
        } catch (error) {
            // console.error('Error calculating routes:', error);
            await this.uxHelperService.showAlert('Error', 'Unable to calculate some routes');
        }
    }
}